// SectionRowActions — the ⋮ on a Groups-tab section head: Rename · Delete.
// Presentational (own open state only); both flows live in GroupsTab via the
// callbacks — both OPTIONAL: the page passes `undefined` when the verb is
// missing and the item doesn't render (absence means hidden). With neither
// grant there is nothing to open (no read item), so the kebab renders nothing.
// Deleting a section keeps its groups — the server moves them to the
// default "Unassigned" section — so it is offered whether or not it holds any.

import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Dropdown, Icon, IconButton } from '@/components/common';

interface SectionRowActionsProps {
  /** Section name — labels the trigger for screen readers. */
  sectionName: string;
  /** Absent = hidden. */
  onRename?: () => void;
  /** Absent = hidden. */
  onDelete?: () => void;
}

const SectionRowActions = ({ sectionName, onRename, onDelete }: SectionRowActionsProps) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  if (!onRename && !onDelete) return null;

  const item = (
    label: string,
    icon: string,
    onClick: () => void,
    opts: { danger?: boolean } = {},
  ): ReactNode => (
    <button
      type='button'
      className={`app-dropdown__item${opts.danger ? ' app-dropdown__item--danger' : ''}`}
      role='menuitem'
      onClick={() => {
        setOpen(false);
        onClick();
      }}
    >
      <Icon name={icon} size={15} />
      <span className='app-dropdown__item-label'>{label}</span>
    </button>
  );

  return (
    <Dropdown
      open={open}
      onClose={() => setOpen(false)}
      align='end'
      width={200}
      portal
      trigger={
        <IconButton
          icon='ellipsis-vertical'
          variant='ghost'
          size='sm'
          aria-label={`${sectionName} — ${t('people_col_actions')}`}
          aria-expanded={open}
          onClick={() => setOpen((o) => !o)}
        />
      }
    >
      {onRename && item(t('section_action_rename'), 'folder-pen', onRename)}

      {onRename && onDelete && <div className='app-dropdown__divider' role='separator' />}

      {onDelete && item(t('section_action_delete'), 'folder-x', onDelete, { danger: true })}
    </Dropdown>
  );
};

export default SectionRowActions;
